"use client";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Minus, Eye, EyeOff, X, ChevronDown, GripVertical, FileText, Plus, Pencil, Trash2, Save, Upload, ArrowLeft, Sparkles, Play, Pause, User, Braces, AArrowUp, Palette, Copy, Building2 } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Textarea } from "@/app/_module/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/app/_module/components/ui/dropdown-menu";
import type { CallScript } from "@/app/_module/services/scriptService";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useCreateScript, useUpdateScript, useDeleteScript, useRewriteScript, useGenerateScript, useDuplicateTemplate } from "@/app/_module/hooks/useCallScripts";
import { SCRIPT_CATEGORIES, PRODUCT_TYPES } from "@/app/_module/constants/scriptCategories";
import type { Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";

const PANEL_POSITION_KEY = "script_panel_position";
const PANEL_SIZE_KEY = "script_panel_size";

const MIN_WIDTH = 300;
const MIN_HEIGHT = 200;
const MAX_WIDTH = 800;
const MAX_HEIGHT = 600;
const DEFAULT_WIDTH = 380;
const DEFAULT_HEIGHT = 450;

const SCROLL_SPEEDS = [
  { label: "Slow", px: 0.5 },
  { label: "Medium", px: 1.2 },
  { label: "Fast", px: 2.5 },
];

const TEXT_SIZES = [
  { label: "S", className: "text-xs leading-relaxed" },
  { label: "M", className: "text-sm leading-relaxed" },
  { label: "L", className: "text-base leading-relaxed" },
  { label: "XL", className: "text-lg leading-relaxed" },
];

// Research-backed colors for focus & readability:
// Dark gray — highest readability on light backgrounds (better than pure black)
// Navy — reduces eye strain, proven in long-reading UX studies
// Dark teal — calming, high contrast, used in teleprompter software
// Warm charcoal — reduces blue light fatigue, easier on eyes for extended reading
const TEXT_COLORS = [
  { label: "Default", hex: null, dot: "bg-foreground" },
  { label: "Navy", hex: "#1e3a5f", dot: "bg-[#1e3a5f]" },
  { label: "Dark Teal", hex: "#115e59", dot: "bg-[#115e59]" },
  { label: "Charcoal", hex: "#3f3f46", dot: "bg-[#3f3f46]" },
  { label: "Deep Blue", hex: "#1e40af", dot: "bg-[#1e40af]" },
];

const LEAD_VARIABLES = [
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "full_name", label: "Full Name" },
  { key: "age", label: "Age" },
  { key: "gender", label: "Gender" },
  { key: "state", label: "State" },
  { key: "city", label: "City" },
  { key: "zip_code", label: "Zip Code" },
  { key: "address", label: "Address" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "dob", label: "Date of Birth" },
  { key: "marital", label: "Marital Status" },
  { key: "occupation", label: "Occupation" },
  { key: "lead_source", label: "Lead Source" },
  { key: "status", label: "Status" },
];

interface ScriptPanelProps {
  scripts: CallScript[];
  selectedScriptId: string | null;
  onSelectScript: (id: string | null) => void;
  onClose: () => void;
  activeLead?: Lead | null;
}

function clamp(v: number, min: number, max: number) {
  return Math.min(Math.max(v, min), max);
}

function load<T>(key: string): T | null {
  try { const s = localStorage.getItem(key); if (s) return JSON.parse(s); } catch {} return null;
}
function save(key: string, v: unknown) {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch {}
}

function getDefaultPos() {
  return {
    x: typeof window !== "undefined" ? window.innerWidth - 480 - DEFAULT_WIDTH : 100,
    y: typeof window !== "undefined" ? window.innerHeight - DEFAULT_HEIGHT - 16 : 100,
  };
}

// Replace [Label] and {{variable}} placeholders with lead data
function fillVariables(content: string, lead: Lead | null | undefined): string {
  if (!lead) return content;
  const vars: Record<string, string> = {
    first_name: lead.firstName || lead.name?.split(" ")[0] || "",
    last_name: lead.lastName || lead.name?.split(" ").slice(1).join(" ") || "",
    name: lead.name || [lead.firstName, lead.lastName].filter(Boolean).join(" ") || "",
    full_name: lead.name || [lead.firstName, lead.lastName].filter(Boolean).join(" ") || "",
    state: lead.state || "",
    city: lead.city || "",
    age: lead.age != null ? String(lead.age) : "",
    phone: lead.phone || "",
    email: lead.email || "",
    gender: lead.gender || "",
    zip: lead.zipCode || "",
    zip_code: lead.zipCode || "",
    address: lead.address || "",
    marital: lead.marital || lead.maritalStatus || "",
    occupation: lead.occupation || "",
    lead_source: lead.listName || "",
    list_name: lead.listName || "",
    status: lead.status || "",
    dob: lead.dob || lead.birthday || "",
  };

  // Map [Label] → key for label-style placeholders
  const labelToKey: Record<string, string> = {};
  for (const v of LEAD_VARIABLES) {
    labelToKey[v.label.toLowerCase()] = v.key;
  }

  // Replace [Label] placeholders (e.g. [First Name], [State])
  let result = content.replace(/\[([^\]]+)\]/g, (match, label) => {
    const key = labelToKey[label.toLowerCase()];
    if (key) {
      const val = vars[key];
      return val || match;
    }
    return match;
  });

  // Also replace legacy {{key}} placeholders
  result = result.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const val = vars[key.toLowerCase()];
    return val || match;
  });

  return result;
}

// Parse ## Section headers from content
function parseSections(content: string): { title: string; offset: number }[] {
  const sections: { title: string; offset: number }[] = [];
  const lines = content.split("\n");
  let charOffset = 0;
  for (const line of lines) {
    const match = line.match(/^##\s+(.+)/);
    if (match) {
      sections.push({ title: match[1].trim(), offset: charOffset });
    }
    charOffset += line.length + 1;
  }
  return sections;
}

type PanelView = "read" | "create" | "edit";

export function ScriptPanel({ scripts, selectedScriptId, onSelectScript, onClose, activeLead }: ScriptPanelProps) {
  const { user } = useUserContext();
  const agencyRole = ((user as any)?.currentAgency?.role || (user as any)?.user?.agencyRole || '').toLowerCase();
  const isOwnerOrAdmin = ['owner', 'admin'].includes(agencyRole) || ((user as any)?.user?.userType || '').toLowerCase() === 'agency_owner';
  const parentAgencyId = (user as any)?.currentAgency?.agency?.parentAgencyId;
  const isRootAgency = !parentAgencyId;

  const [isMinimized, setIsMinimized] = useState(false);
  // Shell chip drag state (touch) + one-time seed flag
  const shellChipSeededRef = useRef(false);
  // Shell chip position is SEPARATE from the panel's: they used to share
  // `pos`, so resetting the panel on reopen teleported the parked pill.
  const shellChipPos = useRef({ x: 12, y: 0 });
  const shellDragRef = useRef<null | { startX: number; startY: number; baseX: number; baseY: number; moved: boolean }>(null);
  const [isSeeThrough, setIsSeeThrough] = useState(false);
  const [view, setView] = useState<PanelView>("read");
  const [formName, setFormName] = useState("");
  const [formContent, setFormContent] = useState("");
  const [formCategory, setFormCategory] = useState("custom");
  const [formProductType, setFormProductType] = useState("");
  const [formShareWithAgency, setFormShareWithAgency] = useState(false);
  const [formScope, setFormScope] = useState("agency-only");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [aiRewrittenContent, setAiRewrittenContent] = useState<string | null>(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const [scrollSpeedIdx, setScrollSpeedIdx] = useState(1);
  const [textSizeIdx, setTextSizeIdx] = useState(1);
  const [textColorIdx, setTextColorIdx] = useState(0);
  const [customColor, setCustomColor] = useState("#1e3a5f");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("all");

  const createScript = useCreateScript();
  const updateScript = useUpdateScript();
  const deleteScript = useDeleteScript();
  const rewriteScript = useRewriteScript();
  const generateScript = useGenerateScript();
  const duplicateTemplate = useDuplicateTemplate();

  const panelRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const autoScrollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // All position/size lives in refs — never causes renders during drag
  // Shell: desktop's stored pixel geometry makes no sense on a phone — seed a
  // floating window that fits (near-full width, ~55% height, under the header).
  const isShellEnv = typeof window !== "undefined" && window.location.pathname.startsWith("/m");
  const pos = useRef(
    isShellEnv
      ? { x: 8, y: 110 }
      : load<{ x: number; y: number }>(PANEL_POSITION_KEY) || getDefaultPos(),
  );
  const sz = useRef(
    isShellEnv
      ? {
          // Open MAXIMIZED (header to dock); the corner handle shrinks it from there.
          width: (typeof window !== "undefined" ? window.innerWidth : 390) - 16,
          height: (typeof window !== "undefined" ? window.innerHeight : 800) - 230,
        }
      : load<{ width: number; height: number }>(PANEL_SIZE_KEY) || { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT },
  );

  const dragging = useRef(false);
  const resizing = useRef(false);
  const origin = useRef({ mx: 0, my: 0, x: 0, y: 0, w: 0, h: 0 });
  const rafId = useRef(0);

  const selectedScript = scripts.find((s) => s.id === selectedScriptId) || null;

  // Clear AI rewrite when switching scripts
  useEffect(() => { setAiRewrittenContent(null); }, [selectedScriptId]);

  // The display content: AI rewrite > variable-filled > raw
  const displayContent = useMemo(() => {
    const raw = aiRewrittenContent || selectedScript?.content || "";
    return fillVariables(raw, activeLead);
  }, [aiRewrittenContent, selectedScript?.content, activeLead]);

  // Parse sections from the display content
  const sections = useMemo(() => parseSections(displayContent), [displayContent]);

  // Filter scripts by active category
  const filteredScripts = useMemo(() => {
    if (activeCategory === "all") return scripts;
    return scripts.filter((s) => s.category === activeCategory);
  }, [scripts, activeCategory]);

  // Category counts for tab badges
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const cat of SCRIPT_CATEGORIES) {
      counts[cat.value] = cat.value === "all" ? scripts.length : scripts.filter((s) => s.category === cat.value).length;
    }
    return counts;
  }, [scripts]);

  // Auto-scroll logic
  useEffect(() => {
    if (isAutoScrolling && scrollContentRef.current) {
      const el = scrollContentRef.current;
      autoScrollTimer.current = setInterval(() => {
        el.scrollTop += SCROLL_SPEEDS[scrollSpeedIdx].px;
        if (el.scrollTop >= el.scrollHeight - el.clientHeight) {
          setIsAutoScrolling(false);
        }
      }, 16); // ~60fps
    }
    return () => {
      if (autoScrollTimer.current) clearInterval(autoScrollTimer.current);
    };
  }, [isAutoScrolling, scrollSpeedIdx]);

  // Apply transform to DOM element (no React render)
  const applyTransform = useCallback(() => {
    const el = panelRef.current || chipRef.current;
    if (!el) return;
    // Shell chip has its OWN position — this effect re-fires on every
    // minimize toggle and was stamping the chip with the PANEL's coords
    // (the reset top-left), teleporting the parked pill.
    const shellChip = el === chipRef.current && typeof window !== "undefined" && window.location.pathname.startsWith("/m");
    const p = shellChip ? shellChipPos.current : pos.current;
    el.style.transform = `translate3d(${p.x}px, ${p.y}px, 0)`;
  }, []);

  const applySize = useCallback(() => {
    if (panelRef.current) {
      panelRef.current.style.width = sz.current.width + "px";
      panelRef.current.style.height = sz.current.height + "px";
    }
  }, []);

  useEffect(() => {
    applyTransform();
    applySize();
  }, [isMinimized, applyTransform, applySize]);

  useEffect(() => {
    function onResize() {
      pos.current.x = clamp(pos.current.x, 0, window.innerWidth - 120);
      pos.current.y = clamp(pos.current.y, 0, window.innerHeight - 40);
      applyTransform();
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [applyTransform]);

  useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!dragging.current && !resizing.current) return;
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        if (dragging.current) {
          pos.current.x = clamp(origin.current.x + (e.clientX - origin.current.mx), 0, window.innerWidth - 120);
          pos.current.y = clamp(origin.current.y + (e.clientY - origin.current.my), 0, window.innerHeight - 40);
          applyTransform();
        }
        if (resizing.current) {
          sz.current.width = clamp(origin.current.w + (e.clientX - origin.current.mx), MIN_WIDTH, MAX_WIDTH);
          sz.current.height = clamp(origin.current.h + (e.clientY - origin.current.my), MIN_HEIGHT, MAX_HEIGHT);
          applySize();
        }
      });
    }
    function onUp() {
      if (dragging.current) {
        dragging.current = false;
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        save(PANEL_POSITION_KEY, pos.current);
      }
      if (resizing.current) {
        resizing.current = false;
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        save(PANEL_SIZE_KEY, sz.current);
      }
    }
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      cancelAnimationFrame(rafId.current);
    };
  }, [applyTransform, applySize]);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    if ((e.target as HTMLElement).closest("input")) return;
    if ((e.target as HTMLElement).closest("textarea")) return;
    e.preventDefault();
    dragging.current = true;
    origin.current = { mx: e.clientX, my: e.clientY, x: pos.current.x, y: pos.current.y, w: 0, h: 0 };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
  }, []);

  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = true;
    origin.current = { mx: e.clientX, my: e.clientY, x: 0, y: 0, w: sz.current.width, h: sz.current.height };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "nwse-resize";
  }, []);

  const handleCreate = () => { setFormName(""); setFormContent(""); setFormCategory("custom"); setFormProductType(""); setFormShareWithAgency(false); setFormScope("agency-only"); setEditingId(null); setView("create"); };

  const handleEdit = (script: CallScript) => { setFormName(script.name); setFormContent(script.content); setFormCategory(script.category || "custom"); setFormProductType(script.productType || ""); setFormShareWithAgency(!!script.agencyId); setFormScope((script as any).scope || "agency-only"); setEditingId(script.id); setView("edit"); };

  const handleSave = async () => {
    const trimmedName = formName.trim();
    const trimmedContent = formContent.trim();
    if (!trimmedName || !trimmedContent) return;
    const shareData = isOwnerOrAdmin ? { shareWithAgency: formShareWithAgency, scope: formShareWithAgency ? formScope : 'personal' } : {};
    if (view === "edit" && editingId) {
      await updateScript.mutateAsync({ id: editingId, data: { name: trimmedName, content: trimmedContent, category: formCategory, ...shareData } });
    } else {
      const created = await createScript.mutateAsync({ name: trimmedName, content: trimmedContent, category: formCategory, productType: formProductType || undefined, ...shareData });
      if (created?.id) onSelectScript(created.id);
    }
    setView("read");
  };

  const handleDelete = async (id: string) => {
    await deleteScript.mutateAsync(id);
    if (selectedScriptId === id) onSelectScript(null);
  };

  const insertVariable = (varKey: string) => {
    const label = LEAD_VARIABLES.find((v) => v.key === varKey)?.label || varKey;
    const insert = `[${label}]`;
    const ta = textareaRef.current;
    if (!ta) {
      setFormContent((prev) => prev + insert);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    setFormContent((prev) => prev.slice(0, start) + insert + prev.slice(end));
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + insert.length;
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result;
      if (typeof text === "string") {
        setFormContent(text);
        if (!formName.trim()) setFormName(file.name.replace(/\.txt$/i, ""));
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleAiGenerate = async (scriptType?: string, callPhase?: string) => {
    const leadData = activeLead ? {
      firstName: activeLead.firstName,
      lastName: activeLead.lastName,
      name: activeLead.name,
      state: activeLead.state,
      city: activeLead.city,
      age: activeLead.age,
      phone: activeLead.phone,
      email: activeLead.email,
      gender: activeLead.gender,
      marital: activeLead.marital || activeLead.maritalStatus,
      occupation: activeLead.occupation,
      dob: activeLead.dob || activeLead.birthday,
      address: activeLead.address,
      zipCode: activeLead.zipCode,
      height: activeLead.height,
      weight: activeLead.weight,
      smokingHistory: activeLead.smokingHistory,
      bloodPressure: activeLead.bloodPressure,
      cholesterol: activeLead.cholesterol,
      familyMedicalHistory: activeLead.familyMedicalHistory,
      existingPolicies: activeLead.existingPolicies,
      employmentStatus: activeLead.employmentStatus,
      notes: activeLead.notes,
      listName: activeLead.listName,
      status: activeLead.status,
    } : undefined;
    const content = await generateScript.mutateAsync({ leadData, scriptType, callPhase });
    if (content) {
      const phaseLabel = callPhase ? SCRIPT_CATEGORIES.find((c) => c.value === callPhase)?.label || callPhase : null;
      const typeLabel = scriptType || "FEX";
      const label = phaseLabel ? `${typeLabel} ${phaseLabel}` : typeLabel;
      const name = activeLead?.firstName
        ? `${label} for ${activeLead.firstName}`
        : `${label} Script`;
      const created = await createScript.mutateAsync({ name, content, category: callPhase || "custom" });
      if (created?.id) onSelectScript(created.id);
    }
  };

  const handleDuplicate = async (id: string) => {
    const created = await duplicateTemplate.mutateAsync(id);
    if (created?.id) onSelectScript(created.id);
  };

  const handleAiRewrite = async () => {
    if (!selectedScript) return;
    const leadData = activeLead ? {
      firstName: activeLead.firstName,
      lastName: activeLead.lastName,
      name: activeLead.name,
      state: activeLead.state,
      city: activeLead.city,
      age: activeLead.age,
      phone: activeLead.phone,
      email: activeLead.email,
      gender: activeLead.gender,
      marital: activeLead.marital || activeLead.maritalStatus,
      occupation: activeLead.occupation,
      dob: activeLead.dob || activeLead.birthday,
      address: activeLead.address,
      zipCode: activeLead.zipCode,
      height: activeLead.height,
      weight: activeLead.weight,
      smokingHistory: activeLead.smokingHistory,
      bloodPressure: activeLead.bloodPressure,
      cholesterol: activeLead.cholesterol,
      familyMedicalHistory: activeLead.familyMedicalHistory,
      existingPolicies: activeLead.existingPolicies,
      employmentStatus: activeLead.employmentStatus,
      notes: activeLead.notes,
      listName: activeLead.listName,
      status: activeLead.status,
    } : undefined;
    const result = await rewriteScript.mutateAsync({ id: selectedScript.id, leadData });
    if (result) setAiRewrittenContent(result);
  };

  const jumpToSection = (offset: number) => {
    const el = scrollContentRef.current;
    if (!el) return;
    // Find the text node position by counting characters
    const textEl = el.querySelector("[data-script-content]") as HTMLElement;
    if (!textEl) return;
    const fullText = textEl.textContent || "";
    const before = fullText.substring(0, offset);
    const linesBefore = before.split("\n").length - 1;
    const lineHeight = 20; // approximate
    el.scrollTop = linesBefore * lineHeight;
  };

  const chipDragged = useRef(false);

  const onChipMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    chipDragged.current = false;
    dragging.current = true;
    origin.current = { mx: e.clientX, my: e.clientY, x: pos.current.x, y: pos.current.y, w: 0, h: 0 };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    const startX = e.clientX;
    const startY = e.clientY;
    const checkDrag = (ev: MouseEvent) => {
      if (Math.abs(ev.clientX - startX) > 4 || Math.abs(ev.clientY - startY) > 4) chipDragged.current = true;
    };
    const cleanup = () => {
      window.removeEventListener("mousemove", checkDrag);
      window.removeEventListener("mouseup", cleanup);
    };
    window.addEventListener("mousemove", checkDrag, { passive: true });
    window.addEventListener("mouseup", cleanup);
  }, []);

  const onChipClick = useCallback(() => {
    if (!chipDragged.current) setIsMinimized(false);
  }, []);

  if (isMinimized) {
    const chipShell = typeof window !== "undefined" && window.location.pathname.startsWith("/m");
    // Shell: seed the chip bottom-left above the dock ONCE, then let touch
    // drags move it anywhere (desktop's chip drags with the mouse).
    if (chipShell && !shellChipSeededRef.current && typeof window !== "undefined") {
      shellChipSeededRef.current = true;
      shellChipPos.current = { x: 12, y: window.innerHeight - 150 };
    }
    const chipXY = chipShell ? shellChipPos.current : pos.current;
    return (
      <div
        ref={chipRef}
        className="fixed z-[55] left-0 top-0 bg-primary text-primary-foreground px-3 py-1.5 rounded-full shadow-lg cursor-grab active:cursor-grabbing flex items-center gap-2 hover:bg-primary/90 transition-colors text-sm font-medium select-none touch-none"
        // width/height auto EXPLICITLY: React reuses the panel's DOM node for
        // this chip, and applySize's inline pixel size survived — rendering
        // the "chip" as a screen-sized ellipse on the shell.
        style={{ transform: `translate3d(${chipXY.x}px, ${chipXY.y}px, 0)`, willChange: "transform", width: "auto", height: "auto" }}
        onMouseDown={chipShell ? undefined : onChipMouseDown}
        onTouchStart={chipShell ? (e) => {
          const t = e.touches[0];
          shellDragRef.current = { startX: t.clientX, startY: t.clientY, baseX: shellChipPos.current.x, baseY: shellChipPos.current.y, moved: false };
        } : undefined}
        onTouchMove={chipShell ? (e) => {
          const d = shellDragRef.current;
          if (!d) return;
          const t = e.touches[0];
          const dx = t.clientX - d.startX, dy = t.clientY - d.startY;
          if (Math.abs(dx) + Math.abs(dy) > 8) d.moved = true;
          shellChipPos.current.x = clamp(d.baseX + dx, 4, window.innerWidth - 120);
          shellChipPos.current.y = clamp(d.baseY + dy, 4, window.innerHeight - 44);
          if (chipRef.current) chipRef.current.style.transform = `translate3d(${shellChipPos.current.x}px, ${shellChipPos.current.y}px, 0)`;
        } : undefined}
        onTouchEnd={chipShell ? () => {
          const d = shellDragRef.current;
          shellDragRef.current = null;
          if (d && !d.moved) {
            // Reopen at the DEFAULT maximized spot, not wherever the pill was
            // parked — restoring at the pill's coords cut the panel off-screen.
            pos.current = { x: 8, y: 110 };
            sz.current = { width: window.innerWidth - 16, height: window.innerHeight - 230 };
            onChipClick();
          }
        } : undefined}
        onClick={chipShell ? undefined : onChipClick}
      >
        <FileText className="h-3.5 w-3.5" />
        {selectedScript?.name || "Script"}
      </div>
    );
  }

  const isSaving = createScript.isPending || updateScript.isPending;

  // Native shell: the desktop panel is positioned by JS pixel coordinates and
  // dragged with mouse events — on a phone that leaves it off-viewport or
  // stuck, i.e. "call scripts don't show up" mid-call. On /m it becomes a
  // fixed sheet (below the header, above the dock) and the drag/resize
  // machinery simply never applies.
  const isMobileShell = typeof window !== "undefined" && window.location.pathname.startsWith("/m");

  return (
    <div
      ref={panelRef}
      className={`fixed z-[55] rounded-lg shadow-2xl flex flex-col overflow-hidden transition-colors duration-200 left-0 top-0 ${
        isSeeThrough
          ? "bg-background/30 backdrop-blur-[2px] border border-primary/20"
          : "bg-background border-2 border-primary/40"
      }`}
      style={isMobileShell ? { willChange: "auto" } : {
        transform: `translate3d(${pos.current.x}px, ${pos.current.y}px, 0)`,
        width: sz.current.width,
        height: sz.current.height,
        willChange: "transform, width, height",
      }}
    >
      {/* Title bar */}
      <div
        className={`flex items-center justify-between px-3 py-2 border-b cursor-grab active:cursor-grabbing select-none flex-shrink-0 touch-none ${
          isSeeThrough ? "bg-background/60 backdrop-blur-sm border-primary/20" : "bg-muted/60"
        }`}
        onMouseDown={onDragStart}
        onTouchStart={(e) => {
          if ((e.target as HTMLElement).closest("button")) return;
          const t = e.touches[0];
          origin.current = { mx: t.clientX, my: t.clientY, x: pos.current.x, y: pos.current.y, w: 0, h: 0 };
          dragging.current = true;
        }}
        onTouchMove={(e) => {
          if (!dragging.current) return;
          const t = e.touches[0];
          pos.current.x = clamp(origin.current.x + (t.clientX - origin.current.mx), 0, window.innerWidth - 80);
          pos.current.y = clamp(origin.current.y + (t.clientY - origin.current.my), 0, window.innerHeight - 60);
          applyTransform();
        }}
        onTouchEnd={() => { dragging.current = false; }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <FileText className="h-4 w-4 text-primary flex-shrink-0" />
          <span className="text-sm font-semibold truncate">
            {view === "create" ? "New Script" : view === "edit" ? "Edit Script" : selectedScript ? <>Call Scripts <span className="text-muted-foreground font-normal">|</span> {selectedScript.name}</> : "Call Scripts"}
          </span>
          {activeLead && view === "read" && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary flex-shrink-0 flex items-center gap-0.5">
              <User className="h-2.5 w-2.5" />
              {activeLead.firstName || activeLead.name?.split(" ")[0] || "Lead"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 hover:bg-muted" onClick={() => setIsMinimized(true)} title="Minimize">
            <Minus className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={`h-6 w-6 p-0 ${isSeeThrough ? "bg-primary/10 text-primary hover:bg-primary/20" : "hover:bg-muted"}`}
            onClick={() => setIsSeeThrough(!isSeeThrough)}
            title={isSeeThrough ? "Make opaque" : "Make see-through"}
          >
            {isSeeThrough ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 hover:bg-red-100" onClick={() => onClose()} title="Close">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Create / Edit form */}
      {(view === "create" || view === "edit") ? (
        <div className={`flex flex-col flex-1 min-h-0 ${isSeeThrough ? "bg-background/20" : ""}`}>
          <div className={`px-3 py-2 border-b flex-shrink-0 flex items-center gap-2 ${isSeeThrough ? "bg-background/30 border-primary/20" : "bg-muted/30"}`}>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setView("read")} title="Back">
              <ArrowLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="text-xs font-medium text-muted-foreground">{view === "create" ? "Create new script" : "Edit script"}</span>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-3 flex flex-col gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Name</label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Script name" className="h-8 text-xs" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Script Type</label>
                <select
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value)}
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {SCRIPT_CATEGORIES.filter((c) => c.value !== "all").map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Product</label>
                <select
                  value={formProductType}
                  onChange={(e) => setFormProductType(e.target.value)}
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">None</option>
                  {PRODUCT_TYPES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {/* Share with agency (owner only) */}
            {isOwnerOrAdmin && (
              <div className="space-y-1.5">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Building2 className="h-3 w-3" /> Share with agency
                  </span>
                  <input type="checkbox" checked={formShareWithAgency} onChange={e => setFormShareWithAgency(e.target.checked)} className="h-3.5 w-3.5 rounded border-gray-300" />
                </label>
                {formShareWithAgency && (
                  <div className={`grid gap-1 ${isRootAgency ? 'grid-cols-3' : 'grid-cols-2'}`}>
                    {[
                      { value: 'agency-only', label: 'My Agency' },
                      { value: 'agency-and-below', label: 'Agency + Subs' },
                      ...(isRootAgency ? [{ value: 'agency-wide', label: 'Agency-Wide' }] : []),
                    ].map(opt => (
                      <button key={opt.value} type="button" onClick={() => setFormScope(opt.value)}
                        className={`px-2 py-1 rounded text-[10px] font-semibold transition-all ${
                          formScope === opt.value ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                        }`}>{opt.label}</button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-muted-foreground">Content</label>
                <div className="flex items-center gap-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] gap-1 text-primary hover:text-primary">
                        <Braces className="h-3 w-3" />
                        Insert Variable
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="z-[60] w-[180px] max-h-[250px] overflow-y-auto" align="end">
                      {LEAD_VARIABLES.map((v) => (
                        <DropdownMenuItem key={v.key} onClick={() => insertVariable(v.key)} className="text-xs">
                          {v.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] gap-1" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="h-3 w-3" />
                    Upload .txt
                  </Button>
                  <input ref={fileInputRef} type="file" accept=".txt" className="hidden" onChange={handleFileUpload} />
                </div>
              </div>
              <Textarea
                ref={textareaRef}
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                placeholder={"Paste or type your call script here...\n\nTip: Use ## Section Name for quick-jump headers\nTip: Use 'Insert Variable' button above to add lead fields"}
                className="flex-1 min-h-[120px] text-xs leading-relaxed resize-none"
              />
            </div>
          </div>
          <div className={`px-3 py-2 border-t flex items-center justify-end gap-2 flex-shrink-0 ${isSeeThrough ? "bg-background/30 border-primary/20" : "bg-muted/30"}`}>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setView("read")}>Cancel</Button>
            <Button size="sm" className="h-7 text-xs gap-1" disabled={!formName.trim() || !formContent.trim() || isSaving} onClick={handleSave}>
              <Save className="h-3 w-3" />
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Script selector + actions */}
          <div className={`px-3 py-2 border-b flex-shrink-0 flex items-center gap-2 ${isSeeThrough ? "bg-background/30 border-primary/20" : "bg-muted/30"}`}>
            {selectedScript && (
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0" onClick={() => onSelectScript(null)} title="Back to home">
                <ArrowLeft className="h-3.5 w-3.5" />
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex-1 justify-between text-xs h-8">
                  <span className="truncate">{selectedScript?.name || "Select a script..."}</span>
                  <ChevronDown className="h-3.5 w-3.5 ml-2 flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="z-[60] w-[280px] max-h-[300px] overflow-y-auto" align="start">
                {filteredScripts.length === 0 ? (
                  <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                    {activeCategory === "all" ? "No scripts yet — create one below" : `No ${SCRIPT_CATEGORIES.find((c) => c.value === activeCategory)?.label || ""} scripts`}
                  </DropdownMenuItem>
                ) : (
                  filteredScripts.map((script) => (
                    <DropdownMenuItem key={script.id} onClick={() => onSelectScript(script.id)} className="text-xs flex items-center justify-between">
                      <span className="truncate">{script.name}</span>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        {script.agencyId && !script.isTemplate && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 flex items-center gap-0.5">
                            <Building2 className="h-2.5 w-2.5" />
                            {(script as any).scope === 'agency-wide' ? 'Agency-Wide' : (script as any).scope === 'agency-and-below' ? 'Agency + Subs' : 'Agency'}
                          </span>
                        )}
                        {script.isTemplate && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">Template</span>}
                        {script.isDefault && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary font-medium">Default</span>}
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" className="h-8 w-8 p-0 flex-shrink-0" onClick={handleCreate} title="New script">
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Category tabs */}
          <div className={`px-3 py-1.5 border-b flex-shrink-0 flex items-center gap-1 overflow-x-auto scrollbar-none ${isSeeThrough ? "bg-background/20 border-primary/20" : "bg-muted/20"}`}>
            {SCRIPT_CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setActiveCategory(cat.value)}
                className={`script-cat-chip min-h-0 min-w-0 text-[10px] px-2 py-1 rounded-full whitespace-nowrap transition-colors flex-shrink-0 flex items-center gap-1 ${
                  activeCategory === cat.value
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {cat.label}
                {categoryCounts[cat.value] > 0 && (
                  <span className={`text-[9px] px-1 rounded-full ${
                    activeCategory === cat.value ? "bg-primary-foreground/20" : "bg-foreground/10"
                  }`}>
                    {categoryCounts[cat.value]}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Section quick-jump bar */}
          {sections.length > 0 && selectedScript && (
            <div className={`px-3 py-1.5 border-b flex-shrink-0 flex items-center gap-1 overflow-x-auto scrollbar-none ${isSeeThrough ? "bg-background/20 border-primary/20" : "bg-muted/20"}`}>
              {sections.map((s, i) => (
                <button
                  key={i}
                  onClick={() => jumpToSection(s.offset)}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 whitespace-nowrap transition-colors flex-shrink-0"
                >
                  {s.title}
                </button>
              ))}
            </div>
          )}

          {/* Script content */}
          <div ref={scrollContentRef} className="flex-1 min-h-0 overflow-y-auto" onWheel={() => { if (isAutoScrolling) setIsAutoScrolling(false); }}>
            <div className="p-4">
              {selectedScript ? (
                <>
                  {aiRewrittenContent && (
                    <div className="flex items-center gap-1.5 mb-3 text-[10px]">
                      <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium flex items-center gap-1">
                        <Sparkles className="h-2.5 w-2.5" />
                        AI Personalized
                      </span>
                      <button onClick={() => setAiRewrittenContent(null)} className="text-muted-foreground hover:text-foreground underline">
                        Show original
                      </button>
                    </div>
                  )}
                  <div
                    data-script-content
                    className={`${TEXT_SIZES[textSizeIdx].className} whitespace-pre-wrap ${isSeeThrough ? "font-medium" : ""}`}
                    style={{
                      color: textColorIdx === TEXT_COLORS.length
                        ? customColor
                        : TEXT_COLORS[textColorIdx].hex || undefined,
                    }}
                  >
                    {displayContent}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full py-12 text-muted-foreground">
                  <FileText className="h-10 w-10 mb-3 opacity-40" />
                  <p className="text-sm font-medium">No script selected</p>
                  <p className="text-xs mt-1 mb-4">Choose a script or let AI generate one</p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={handleCreate}>
                      <Plus className="h-3.5 w-3.5" />
                      Write my own
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          className="text-xs gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground"
                          disabled={generateScript.isPending}
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                          {generateScript.isPending ? "Generating..." : "AI Generate"}
                          <ChevronDown className="h-3 w-3 ml-0.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="center" className="w-56 z-[9999]" side="top">
                        <DropdownMenuLabel className="text-[10px] text-muted-foreground font-normal">Full Script by Product</DropdownMenuLabel>
                        {PRODUCT_TYPES.map((p) => (
                          <DropdownMenuItem key={p.value} onClick={() => handleAiGenerate(p.label)} className="text-xs">
                            {p.label}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-[10px] text-muted-foreground font-normal">By Call Phase</DropdownMenuLabel>
                        {SCRIPT_CATEGORIES.filter((c) => c.value !== "all" && c.value !== "custom").map((phase) => (
                          <DropdownMenuSub key={phase.value}>
                            <DropdownMenuSubTrigger className="text-xs">{phase.label}</DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="z-[9999]">
                              {PRODUCT_TYPES.map((p) => (
                                <DropdownMenuItem key={p.value} onClick={() => handleAiGenerate(p.label, phase.value)} className="text-xs">
                                  {p.label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  {activeLead && (
                    <p className="text-[10px] mt-2 text-primary">
                      Will personalize for {activeLead.firstName || activeLead.name}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Bottom action bar */}
          {selectedScript && (
            <div className={`px-3 py-1.5 border-t flex items-center justify-between gap-1 flex-shrink-0 ${isSeeThrough ? "bg-background/30 border-primary/20" : "bg-muted/30"}`}>
              <div className="flex items-center gap-1">
                {/* Auto-scroll controls */}
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-6 px-2 text-[10px] gap-1 ${isAutoScrolling ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"}`}
                  onClick={() => setIsAutoScrolling(!isAutoScrolling)}
                  title={isAutoScrolling ? "Pause auto-scroll" : "Start auto-scroll"}
                >
                  {isAutoScrolling ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                  {SCROLL_SPEEDS[scrollSpeedIdx].label}
                </Button>
                {isAutoScrolling && (
                  <button
                    onClick={() => setScrollSpeedIdx((scrollSpeedIdx + 1) % SCROLL_SPEEDS.length)}
                    className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground hover:text-foreground"
                  >
                    Speed
                  </button>
                )}
                {/* AI Rewrite */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[10px] gap-1 text-muted-foreground hover:text-primary"
                  onClick={handleAiRewrite}
                  disabled={rewriteScript.isPending}
                  title={activeLead ? `AI personalize for ${activeLead.firstName || activeLead.name}` : "AI improve script"}
                >
                  <Sparkles className="h-3 w-3" />
                  {rewriteScript.isPending ? "Rewriting..." : "AI Rewrite"}
                </Button>
                {/* Text size */}
                <button
                  onClick={() => setTextSizeIdx((textSizeIdx + 1) % TEXT_SIZES.length)}
                  className="h-6 px-1.5 text-[9px] rounded text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-0.5"
                  title={`Text size: ${TEXT_SIZES[textSizeIdx].label}`}
                >
                  <AArrowUp className="h-3 w-3" />
                  {TEXT_SIZES[textSizeIdx].label}
                </button>
                {/* Text color */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="h-6 px-1.5 text-[9px] rounded text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1"
                      title="Text color"
                    >
                      <Palette className="h-3 w-3" />
                      <span
                        className="w-2 h-2 rounded-full border border-border"
                        style={{
                          backgroundColor: textColorIdx === TEXT_COLORS.length
                            ? customColor
                            : TEXT_COLORS[textColorIdx].hex || "currentColor",
                        }}
                      />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="z-[9999] min-w-0">
                    {TEXT_COLORS.map((c, i) => (
                      <DropdownMenuItem key={c.label} onClick={() => setTextColorIdx(i)} className="text-xs gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full border border-border"
                          style={{ backgroundColor: c.hex || "currentColor" }}
                        />
                        {c.label}
                      </DropdownMenuItem>
                    ))}
                    {/* Custom color picker */}
                    <div className="px-2 py-1.5 flex items-center gap-2 border-t">
                      <input
                        type="color"
                        value={customColor}
                        onChange={(e) => {
                          setCustomColor(e.target.value);
                          setTextColorIdx(TEXT_COLORS.length);
                        }}
                        className="w-5 h-5 rounded cursor-pointer border-0 p-0 bg-transparent"
                        title="Custom color"
                      />
                      <span className="text-xs text-muted-foreground">Custom</span>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex items-center gap-1">
                {selectedScript.isTemplate ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[10px] gap-1 text-primary hover:text-primary hover:bg-primary/10"
                    onClick={() => handleDuplicate(selectedScript.id)}
                    disabled={duplicateTemplate.isPending}
                  >
                    <Copy className="h-3 w-3" />
                    {duplicateTemplate.isPending ? "Duplicating..." : "Duplicate to My Scripts"}
                  </Button>
                ) : (
                  <>
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] gap-1 text-muted-foreground hover:text-foreground" onClick={() => handleEdit(selectedScript)}>
                      <Pencil className="h-3 w-3" />
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] gap-1 text-muted-foreground hover:text-destructive" onClick={() => setDeleteConfirmId(selectedScript.id)} disabled={deleteScript.isPending}>
                      <Trash2 className="h-3 w-3" />
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Resize grip */}
      <div
        className="absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize flex items-end justify-end p-0.5 touch-none"
        onMouseDown={onResizeStart}
        onTouchStart={(e) => {
          e.stopPropagation();
          const t = e.touches[0];
          origin.current = { mx: t.clientX, my: t.clientY, x: 0, y: 0, w: sz.current.width, h: sz.current.height };
          resizing.current = true;
        }}
        onTouchMove={(e) => {
          if (!resizing.current) return;
          const t = e.touches[0];
          sz.current.width = clamp(origin.current.w + (t.clientX - origin.current.mx), 260, window.innerWidth - 8);
          sz.current.height = clamp(origin.current.h + (t.clientY - origin.current.my), 260, window.innerHeight - 120);
          applySize();
        }}
        onTouchEnd={() => { resizing.current = false; }}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" className="text-muted-foreground/50">
          <path d="M9 1L1 9M9 5L5 9M9 9L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>

      {/* Centered delete confirmation */}
      {deleteConfirmId && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-lg">
          <div className="flex flex-col items-center gap-3 p-5">
            <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-destructive" />
            </div>
            <p className="text-sm font-medium">Delete this script?</p>
            <p className="text-xs text-muted-foreground text-center">This cannot be undone.</p>
            <div className="flex items-center gap-2 mt-1">
              <Button variant="outline" size="sm" className="text-xs h-7 px-3" onClick={() => setDeleteConfirmId(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="text-xs h-7 px-3 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  handleDelete(deleteConfirmId);
                  setDeleteConfirmId(null);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
